import { motion } from 'motion/react';
import { mascaraCPF } from '@/pages/inscricao/utils';
import CheckAnimado from '@/components/movimento/CheckAnimado';
import { tituloResultado } from '../utils';
import type { ResultadoLeitura } from '../types';

interface ResultadoTelaCheiaProps {
  resultado: ResultadoLeitura;
  fotoUrl: string | null;
  isDono: boolean;
  recebendo: boolean;
  aoReceberLiberar: () => void;
  aoVoltar: () => void;
}

const CORES: Record<string, string> = {
  entrou: 'bg-success-600',
  ja_entrou: 'bg-danger-600',
  nao_pago: 'bg-accent-500',
  cancelada: 'bg-danger-600',
  nao_encontrado: 'bg-foreground-800',
  outro_evento: 'bg-foreground-800',
  lista_espera: 'bg-accent-600',
  nao_liberado: 'bg-danger-600',
};

// Resultado do bipe: folha que sobe com a resposta bem grande (verde = pode
// entrar / vermelho = barrado). Some sozinho em 2 s, menos quando exige ação.
export default function ResultadoTelaCheia({
  resultado,
  fotoUrl,
  isDono,
  recebendo,
  aoReceberLiberar,
  aoVoltar,
}: ResultadoTelaCheiaProps) {
  const cor = CORES[resultado.situacao] ?? 'bg-foreground-800';
  const titulo = tituloResultado(resultado);
  const precisaAcao = resultado.situacao === 'nao_pago';
  const claro = resultado.situacao === 'nao_pago';
  // Vermelho (barrado) dá o tremido de "não pode"; verde não treme.
  const barrado =
    resultado.situacao !== 'entrou' &&
    resultado.situacao !== 'nao_pago' &&
    resultado.situacao !== 'lista_espera';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div
        className={`absolute inset-0 ${
          resultado.situacao === 'entrou'
            ? 'bg-success-700/60'
            : barrado
              ? 'bg-danger-700/55'
              : 'bg-black/45'
        }`}
        onClick={precisaAcao ? undefined : aoVoltar}
        aria-hidden
      />
      <div
        className={`folha-sobe relative z-10 w-full max-w-[560px] overflow-hidden rounded-t-[28px] ${cor} ${
          claro ? 'text-warning-950' : 'text-white'
        }`}
      >
        <div className={`mx-auto mt-3 h-1 w-10 rounded-full ${claro ? 'bg-black/20' : 'bg-white/40'}`} />

        <div className="flex flex-col items-center px-6 pb-6 pt-5 text-center">
          <motion.span
            className={`flex h-24 w-24 items-center justify-center rounded-full ${
              claro ? 'bg-black/10' : 'bg-white/20'
            }`}
            animate={barrado ? { x: [0, -4, 4, -4, 4, 0] } : undefined}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            <CheckAnimado
              estado={barrado ? 'erro' : 'ok'}
              tamanho={62}
              cor={claro ? '#5a3d00' : '#ffffff'}
              fundo="transparent"
            />
          </motion.span>

          <h1 className="mt-4 text-[34px] font-extrabold uppercase leading-none tracking-tight">
            {titulo}
          </h1>

          {resultado.participante && (
            <p className="mt-4 text-[24px] font-bold leading-tight">{resultado.participante}</p>
          )}

          <div className="mt-1 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            {resultado.cpf && (
              <p className={`text-[15px] font-medium ${claro ? 'text-warning-900' : 'text-white/85'}`}>
                CPF {mascaraCPF(resultado.cpf)}
              </p>
            )}
            {resultado.participacao && (
              <p className={`text-[15px] ${claro ? 'text-warning-900' : 'text-white/70'}`}>
                {resultado.participacao}
              </p>
            )}
          </div>

          {fotoUrl && (
            <div
              className={`mt-5 flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border-4 ${
                claro ? 'border-black/10 bg-black/5' : 'border-white/40 bg-white/10'
              }`}
            >
              <img
                src={fotoUrl}
                alt={resultado.participante ?? 'Participante'}
                className="h-full w-full object-cover"
              />
            </div>
          )}

          {precisaAcao && (
            <div className="mt-6 w-full max-w-[380px] space-y-2">
              {isDono && (
                <button
                  type="button"
                  onClick={aoReceberLiberar}
                  disabled={recebendo}
                  className="aperta w-full cursor-pointer whitespace-nowrap rounded-[18px] bg-background-50 px-6 py-4 text-[17px] font-bold text-warning-700 disabled:opacity-60"
                >
                  {recebendo ? 'Registrando…' : 'Receber e liberar'}
                </button>
              )}
              <button
                type="button"
                onClick={aoVoltar}
                disabled={recebendo}
                className={`aperta w-full cursor-pointer whitespace-nowrap rounded-[18px] border-2 px-6 py-3 text-[16px] font-semibold disabled:opacity-60 ${
                  claro ? 'border-black/25 text-warning-900' : 'border-white/60 text-white'
                }`}
              >
                Voltar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}